import React, { useState } from "react";
import { Button, Modal, message } from "antd";
import { CreditCardOutlined, CheckCircleFilled, GiftOutlined, SwapOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import useAuthStore from "../../../../../stores/useAuthStore";
import useCartStore from "../../../../../stores/useCartStore";
import useSessionStore from "../../../../../stores/useSessionStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";
import { trace } from "../../../../../utils/debugTrace";

const ReturnOptionsModal = ({
  showReturnOptions,
  setShowReturnOptions,
  selectedReturnType,
  setSelectedReturnType,
  cart,
  setCart,
  posConfig,
  setLoading,
  setIsPurchaseReturn,
  setStartRowData,
  setIsInputFocused,
  addSelectedReturnProductsToCart,
  tillDataPaymentMethods,
  setSelectedPaymentMethod,
  setIsRefundWalletOpen,
  setFilterDrawer,
  selectedReturnOrder,
  isBlindReceiptPending,
  setIsBlindReceiptPending,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const tillData = useAuthStore((s) => s.tillData);

  const tillValue = useAuthStore.getState().tillValue;
  const tillDocumentSequence = useSessionStore.getState().documentSequence;
  const userData = useAuthStore.getState().userData;
  const defaultCustomer = tillData?.tillAccess?.csBunit?.b2cCustomer;

  // BUG-1643: selectedReturnOrder.cwrB2cCustomer is the raw backend order
  // customer record — it carries NRI status only inside metaData ("Nri
  // Billing":"true"), never in a flattened customer.nriBilling boolean the
  // way the normal customer-search path (useCustomerOperations.js) produces
  // it. Without this normalization, checkConditionalRules/computeTCS see
  // nriBilling as falsy for a genuine NRI customer on the exchange cart,
  // route them through the wrong (non-NRI) drawer gate, and TCS ends up
  // applying via the ordinary PAN-rate branch with no NRI confirmation at all.
  const resolveNriBilling = (rawCustomer) => {
    if (rawCustomer?.nriBilling === true) return true;
    const nriBillingMeta = rawCustomer?.metaData?.find((m) => m?.key?.toLowerCase()?.replace(/\s+/g, "") === "nribilling");
    return nriBillingMeta?.value != null && nriBillingMeta?.value !== "" ? String(nriBillingMeta.value).toLowerCase() === "true" : false;
  };

  const createExchangeCartObj = () => {
    const rawExchangeCustomer = selectedReturnOrder?.cwrB2cCustomer || defaultCustomer;
    return {
      items: [],
      couponInput: [],
      advancePayment: 0,
      total: 0,
      tax: 0,
      discount: 0,
      paid: 0,
      change: 0,
      totalQty: 0,
      roundOff: 0,
      payments: [],
      redemptionPoints: 0,
      accumulationPoints: 0,
      creditAmount: 0,
      docType: "Invoice",
      sOrderID: uuidv4().replace(/-/g, "").toUpperCase(),
      referenceId: uuidv4().replace(/-/g, "").toUpperCase(),
      giftCardRefId: uuidv4().replace(/-/g, "").toUpperCase(),
      couponRefId: uuidv4().replace(/-/g, "").toUpperCase(),
      customer: { ...rawExchangeCustomer, nriBilling: resolveNriBilling(rawExchangeCustomer) },
      salesRepId: null,
      cardPaymentData: {},
      documentno: `${tillData?.tillAccess?.cwrTill?.prefix}${tillDocumentSequence + 1}`,
      userName: userData?.user,
      tillName: tillValue?.name,
      subTotal: 0,
      itemsSummery: {},
      amountInWords: "",
      groupedItemsByTaxRate: {},
      serviceCharge: 0,
      tcs: 0,
      tip: 0,
      deliveryCharge: 0,
      packingCharge: 0,
      others: 0,
      refundAmount: 0,
    };
  };

  const handleCancel = () => {
    setShowReturnOptions(false);
    setSelectedReturnType(null);
    // BUG-1213: Reset the pending flag — cart was NOT modified yet, so no cleanup needed
    if (isBlindReceiptPending) {
      setIsBlindReceiptPending(false);
    }
    // Legacy safety: clean up isReturn/isBlindReceipt if they were set by old code paths
    const cartData = { ...useCartStore.getState().cart };
    if (cartData?.isReturn === true) {
      delete cartData.isReturn;
    }
    if (cartData?.isBlindReceipt === true) {
      delete cartData.isBlindReceipt;
    }
    setCart(cartData);
  };

  const handleContinue = async () => {
    // BUG-1234: Prevent multiple clicks from triggering the return flow repeatedly.
    if (submitting) return;
    setSubmitting(true);
    try {
      // BUG-1213: Use isBlindReceiptPending flag instead of checking cart state.
      // Cart.isReturn is now set HERE (after user confirms) rather than before the modal opens.
      if (isBlindReceiptPending) {
        // Apply isReturn + isBlindReceipt to cart NOW that user has confirmed
        const currentCart = useCartStore.getState().cart;
        const updatedCart = {
          ...currentCart,
          isReturn: true,
          isBlindReceipt: true,
        };
        setCart(updatedCart);
        useCartStore.getState().setCart(updatedCart);
        setIsBlindReceiptPending(false);

        setFilterDrawer(true);
        if (selectedReturnType === "EXCHANGE") {
          // Every isExchange check downstream (cart.js, useCartHandlers.js,
          // useProductOperations.js) is gated on selectedReturnType==="EXCHANGE" &&
          // !returnThisReceipt. Setting returnThisReceipt here — before the
          // exchangeCartObj created below is even used — made the first item added
          // to it get routed into the return cart instead, with return semantics
          // (negative weight, unmodifiable qty) (#1926).
          const exchangeCartObj = createExchangeCartObj();
          useCartStore.getState().setExchangeCart(exchangeCartObj);
          trace("SMAddProduct", "ReturnOptionsModal: EXCHANGE selected (blind receipt)", {
            exchangeCartSOrderID: exchangeCartObj.sOrderID,
            returnThisReceiptAfter: useTransactionStore.getState().returnThisReceipt,
          });
        } else {
          useTransactionStore.getState().setReturnThisReceipt(true);
        }
        useTransactionStore.getState().setSelectedReturnType(selectedReturnType);
        setShowReturnOptions(false);
      } else {
        if (selectedReturnType === "REFUND") {
          setLoading(true);
          setIsPurchaseReturn(false);
          setStartRowData({ startRow: "0", endRow: "10" });
          setIsInputFocused(true);
          const result = await addSelectedReturnProductsToCart();
          if (!result) return;
          setShowReturnOptions(false);
        } else if (selectedReturnType === "STORE_CREDIT") {
          setIsPurchaseReturn(false);
          useTransactionStore.getState().setSelectedReturnType(selectedReturnType);
          const updatedCart = await addSelectedReturnProductsToCart();
          if (!updatedCart) return;
          const walletPayment = tillDataPaymentMethods.find((pm) => pm.isWallet === "Y");
          if (walletPayment && updatedCart?.customer?.cwrWallet?.wallet_Id) {
            setSelectedPaymentMethod(walletPayment);
          }
          setIsRefundWalletOpen(true);
        } else if (selectedReturnType === "EXCHANGE") {
          setIsPurchaseReturn(false);
          const exchangeCartObj = createExchangeCartObj();
          useCartStore.getState().setExchangeCart(exchangeCartObj);
          const result = await addSelectedReturnProductsToCart();
          if (!result) return;
        }
        useTransactionStore.getState().setSelectedReturnType(selectedReturnType);
        setShowReturnOptions(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={showReturnOptions}
      closable={true}
      footer={null}
      centered
      width="35%"
      onCancel={handleCancel}
      styles={{ body: { padding: "20px", backgroundColor: "white", borderRadius: "8px" } }}
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: "16px", fontWeight: 600 }}>How would you like to return?</div>
        <div style={{ fontSize: "13px", color: "#6B7280" }}>Choose the return option that works best for you</div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {posConfig?.allowReturnRefund === "Y" && (
          <div
            onClick={() => setSelectedReturnType("REFUND")}
            style={{
              background: "#fff",
              border: selectedReturnType === "REFUND" ? "1px solid #3B82F6" : "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ background: "#F3F4F6", padding: 3, borderRadius: 8 }}>
                <CreditCardOutlined style={{ fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>Return for Refund</div>
              </div>
            </div>
            {selectedReturnType === "REFUND" && <CheckCircleFilled style={{ color: "#3B82F6", fontSize: 18 }} />}
          </div>
        )}

        {posConfig?.allowReturnCreditNote === "Y" && (
          <div
            onClick={() => setSelectedReturnType("STORE_CREDIT")}
            style={{
              background: "#fff",
              border: selectedReturnType === "STORE_CREDIT" ? "1px solid #3B82F6" : "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ background: "#F3F4F6", padding: 3, borderRadius: 8 }}>
                <GiftOutlined style={{ fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>Return for Store Credit</div>
              </div>
            </div>
            {selectedReturnType === "STORE_CREDIT" && <CheckCircleFilled style={{ color: "#3B82F6", fontSize: 18 }} />}
          </div>
        )}

        {posConfig?.allowReturnExchange === "Y" && (
          <div
            onClick={() => setSelectedReturnType("EXCHANGE")}
            style={{
              background: "#fff",
              border: selectedReturnType === "EXCHANGE" ? "1px solid #3B82F6" : "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ background: "#F3F4F6", padding: 3, borderRadius: 8 }}>
                <SwapOutlined style={{ fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 500 }}>Return & Exchange</div>
              </div>
            </div>
            {selectedReturnType === "EXCHANGE" && <CheckCircleFilled style={{ color: "#3B82F6", fontSize: 18 }} />}
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <Button onClick={handleCancel} style={{ borderColor: "#92909880", borderRadius: "5px", height: "6vh" }}>
          Cancel
        </Button>
        <Button
          type="primary"
          disabled={!selectedReturnType || submitting}
          loading={submitting}
          onClick={handleContinue}
          style={{
            backgroundColor: "#2F3856",
            borderColor: "#2F3856",
            borderRadius: "5px",
            color: "white",
            height: "6vh",
            opacity: !selectedReturnType || submitting ? 0.45 : 1,
          }}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

export default ReturnOptionsModal;
